import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Clock, CheckCircle, XCircle, Eye } from 'lucide-react';

interface ChangeRequestApprovalPanelProps {
  workspaceId: string;
  projectId?: string;
}

export function ChangeRequestApprovalPanel({ workspaceId, projectId }: ChangeRequestApprovalPanelProps) {
  const { data: pendingRequests = [] } = useQuery({
    queryKey: ['change-management-pending', workspaceId, projectId],
    queryFn: async () => {
      let query = supabase
        .from('tum_change_management_requests')
        .select('*')
        .eq('workspace_id', workspaceId)
        .in('status', ['pending', 'under_review'])
        .order('created_at', { ascending: true });

      if (projectId) {
        query = query.eq('onboarding_project_id', projectId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!workspaceId,
  });

  if (pendingRequests.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Pending Approvals
          <Badge variant="destructive" className="ml-2">
            {pendingRequests.length}
          </Badge>
        </CardTitle>
        <CardDescription>
          Review and approve change management requests
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {pendingRequests.map((request: any) => (
            <div key={request.id} className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{request.request_type.replace('_', ' ')}</span>
                  <Badge variant="secondary">{request.priority}</Badge>
                </div>
                {request.description && (
                  <p className="text-sm text-muted-foreground mt-1">{request.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm">
                  <Eye className="h-4 w-4 mr-2" />
                  Review
                </Button>
                <Button variant="default" size="sm">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve
                </Button>
                <Button variant="destructive" size="sm">
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

